import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (mobile, password) =>
    api.post('/auth/login', { mobile, password }),
  register: (mobile, fullName, password, role, email) =>
    api.post('/auth/register', { mobile, fullName, password, role, email }),
  getMe: () => api.get('/auth/me'),
  changePassword: (oldPassword, newPassword) =>
    api.post('/auth/change-password', { oldPassword, newPassword }),
};

export const barcodeService = {
  validate: (barcodeNumber) =>
    api.get(`/barcodes/${barcodeNumber}/validate`),
  validateForUpdate: (barcodeNumber) =>
    api.get(`/barcodes/${barcodeNumber}/validate-for-update`),
  bulkImport: (formData) =>
    api.post('/barcodes/bulk-import', formData, {
      headers: { 'Content-Type': undefined },
    }),
  getStatistics: () => api.get('/barcodes/summary'),
  getAll: (status, limit = 50, page = 1, search = null) =>
    api.get('/barcodes', { params: { status, limit, page, search } }),
  activate: (id) => api.put(`/barcodes/${id}/activate`),
  deactivate: (id) => api.put(`/barcodes/${id}/deactivate`),
};

export const recordService = {
  create: (data) => api.post('/records', data),
  getById: (id) => api.get(`/records/${id}`),
  getByBarcode: (barcodeNumber) =>
    api.get(`/records/barcode/${barcodeNumber}`),
  getAll: (filters = {}, limit = 50, page = 1) =>
    api.get('/records', { params: { ...filters, limit, page } }),
  update: (id, data) => api.put(`/records/${id}`, data),
  updateStatus: (idOrBarcode, data) => {
    // Check if it's a UUID (format: 8-4-4-4-12 hex chars) or a barcode/number
    const isUUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(idOrBarcode);
    const endpoint = isUUID
      ? `/records/${idOrBarcode}/status`
      : `/records/barcode/${idOrBarcode}/status`;
    return api.put(endpoint, data);
  },
  getStatistics: (startDate, endDate) =>
    api.get('/records/statistics', { params: { startDate, endDate } }),
};

export const userService = {
  create: (data) =>
    api.post('/users', data),
  getAll: (role, status) =>
    api.get('/users', { params: { role, status } }),
  getById: (id) => api.get(`/users/${id}`),
  updateUser: (id, data) =>
    api.put(`/users/${id}`, data),
  updateStatus: (id, status) =>
    api.put(`/users/${id}/status`, { status }),
  deleteUser: (id) =>
    api.delete(`/users/${id}`),
};

export const apiService = {
  // Auth methods
  login: authService.login,
  register: authService.register,
  getMe: authService.getMe,
  
  // User profile methods
  updateUser: (userId, data) =>
    api.put(`/users/${userId}`, data),
  changePassword: (data) => {
    // Convert frontend field names to backend field names
    const backendData = {
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    };
    return api.post('/auth/change-password', backendData);
  },
  
  // Barcode methods
  validateBarcode: barcodeService.validate,
  validateBarcodeForUpdate: barcodeService.validateForUpdate,
  bulkImportBarcodes: barcodeService.bulkImport,
  getBarcodeStatistics: barcodeService.getStatistics,
  getAllBarcodes: barcodeService.getAll,
  activateBarcode: barcodeService.activate,
  deactivateBarcode: barcodeService.deactivate,
  
  // Record methods
  createRecord: recordService.create,
  getRecordById: recordService.getById,
  getRecordByBarcode: recordService.getByBarcode,
  getAllRecords: recordService.getAll,
  updateRecord: recordService.update,
  updateRecordStatus: recordService.updateStatus,
  getRecordStatistics: recordService.getStatistics,
  
  // User management methods
  createUser: userService.create,
  getAllUsers: userService.getAll,
  getUserById: userService.getById,
  updateUserProfile: userService.updateUser,
  updateUserStatus: userService.updateStatus,
  deleteUser: userService.deleteUser,
};

export default api;
